import React from 'react';

import AmountDisplay from '../../../components/amount/AmountDisplay';
import {
    formatDueDate,
    formatStatusTone,
    formatTaskAssignee,
} from '../formatting';
import { formatTaskStatus, resolveTaskStatus } from '../helpers';

const taskRowId = (task) => {
    const raw = task?.id;
    const n = Number(raw);
    if (Number.isFinite(n) && String(n) === String(raw).trim()) {
        return `TASK-${String(n).padStart(3, '0')}`;
    }
    return `TASK-${String(raw ?? '').slice(0, 8).toUpperCase()}`;
};

const TaskCardCompact = ({
    task,
    tokenSymbol,
    onOpenDetail,
    showAssignee = true,
}) => {
    const resolvedStatus = resolveTaskStatus(task);
    const assignee = formatTaskAssignee(task);
    const title = String(task?.title || 'Untitled task').trim();

    const open = () => onOpenDetail?.(task);

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={open}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    open();
                }
            }}
            className="group relative cursor-pointer rounded-xl border border-zinc-200 bg-zinc-50/80 px-3 py-2.5 shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-100/90 dark:border-zinc-800 dark:bg-zinc-900/50 dark:hover:border-zinc-700 dark:hover:bg-zinc-900/70 sm:px-4 sm:py-3"
        >
            <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                            {taskRowId(task)}
                        </span>
                        <span className={formatStatusTone(resolvedStatus)}>
                            {formatTaskStatus(resolvedStatus)}
                        </span>
                        {task.escrowId ? (
                            <span className="rounded border border-teal-200/80 bg-teal-50 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide text-teal-800 dark:border-teal-800/50 dark:bg-teal-950/40 dark:text-teal-300">
                                Escrow #{task.escrowId}
                            </span>
                        ) : null}
                    </div>
                    <h3 className="mt-1 truncate text-sm font-semibold text-zinc-900 dark:text-white sm:text-[15px]">
                        {title}
                    </h3>
                    {showAssignee ? (
                        <p className="mt-0.5 truncate text-sm text-zinc-600 dark:text-zinc-400">
                            <span className="font-medium text-zinc-500 dark:text-zinc-500">
                                Assignee:{' '}
                            </span>
                            <span className="text-zinc-800 dark:text-zinc-200">
                                {assignee.primary}
                            </span>
                        </p>
                    ) : null}
                </div>

                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                    <div className="hidden text-right sm:block">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                            Amount
                        </p>
                        <AmountDisplay
                            value={task.amountFormatted ?? task.amountLabel}
                            className="text-sm font-bold text-zinc-900 dark:text-white"
                            suffix={tokenSymbol}
                        />
                        <p className="mt-0.5 flex items-center justify-end gap-1 text-[11px] text-zinc-600 dark:text-zinc-400">
                            <i className="fa-regular fa-calendar text-[10px] text-zinc-400" />
                            {formatDueDate(task.deadline)}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            open();
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-teal-700 shadow-sm transition-colors hover:border-teal-300 hover:bg-teal-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-teal-400 dark:hover:border-teal-700 dark:hover:bg-teal-950/50"
                    >
                        <i className="fa-regular fa-eye text-[10px]" />
                        <span className="hidden sm:inline">Details</span>
                    </button>

                    <i className="fa-solid fa-chevron-right text-xs text-zinc-400 transition-transform group-hover:translate-x-0.5 dark:text-zinc-600" />
                </div>
            </div>

            <div className="mt-2 flex items-center justify-between gap-3 border-t border-zinc-200/80 pt-2 text-[11px] dark:border-zinc-800/60 sm:hidden">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                        Amount
                    </p>
                    <AmountDisplay
                        value={task.amountFormatted ?? task.amountLabel}
                        className="font-semibold text-zinc-900 dark:text-white"
                        suffix={tokenSymbol}
                        suffixClassName="font-medium text-zinc-500"
                    />
                </div>
                <p className="flex items-center gap-1 text-right text-zinc-600 dark:text-zinc-400">
                    <i className="fa-regular fa-calendar text-[10px] text-zinc-400" />
                    {formatDueDate(task.deadline)}
                </p>
            </div>
        </div>
    );
};

export default TaskCardCompact;
